/**
 * The Kalendarz tab's presses as one machine: the redirect to Google, the disconnect confirm, the
 * one write, the sentence after it, and where focus goes when the pressed control is gone. Pure.
 *
 * One act at a time. Nothing opens, closes or starts while one is in flight except its answer.
 */

import type { GoogleConnectOutcome } from './googleCalendar';

/** What the read shows right now; `none` is waiting, failed or unavailable. */
export type GoogleCalendarFace = 'none' | 'notConnected' | 'connected' | 'broken';

export type GoogleCalendarProblem = 'connectFailed' | 'scopeMissing' | 'disconnectFailed';

export interface GoogleCalendarFlow {
  /** `connect` lasts until the document leaves for Google. */
  readonly busy: 'connect' | 'disconnect' | null;
  readonly confirm: boolean;
  readonly problem: GoogleCalendarProblem | null;
  /** „Połącz z Google" replaced the pressed „Odłącz" and takes focus as it mounts. */
  readonly focusConnect: boolean;
}

export type GoogleCalendarFlowEvent =
  | { readonly type: 'CONNECT'; readonly face: GoogleCalendarFace }
  | { readonly type: 'CONNECT_FAILED' }
  /** The page came back from the back-forward cache with the redirect still pending. */
  | { readonly type: 'RESTORED' }
  | { readonly type: 'ASK_DISCONNECT'; readonly face: GoogleCalendarFace }
  | { readonly type: 'CANCEL' }
  | { readonly type: 'CONFIRM' }
  /** The DELETE answered; `connected` is the answer's own flag. */
  | { readonly type: 'DISCONNECTED'; readonly connected: boolean }
  | { readonly type: 'DISCONNECT_FAILED' }
  /** What the read shows changed — refetched elsewhere, or failed. */
  | { readonly type: 'FACE'; readonly face: GoogleCalendarFace }
  | { readonly type: 'DEACTIVATED' }
  /** The tab is on screen again; nothing was just pressed on it. */
  | { readonly type: 'ACTIVATED' }
  | { readonly type: 'FOCUS_TAKEN' };

export const GOOGLE_CALENDAR_IDLE: GoogleCalendarFlow = {
  busy: null,
  confirm: false,
  problem: null,
  focusConnect: false,
};

/** The machine as the tab opens after the return page. */
export function googleCalendarFlowAfterReturn(
  outcome: GoogleConnectOutcome | null,
): GoogleCalendarFlow {
  const problem =
    outcome === 'scopeMissing' ? 'scopeMissing' : outcome === 'failed' ? 'connectFailed' : null;
  return { ...GOOGLE_CALENDAR_IDLE, problem };
}

const connectable = (face: GoogleCalendarFace) => face === 'notConnected' || face === 'broken';
const disconnectable = (face: GoogleCalendarFace) => face === 'connected' || face === 'broken';

export function googleCalendarFlow(
  state: GoogleCalendarFlow,
  event: GoogleCalendarFlowEvent,
): GoogleCalendarFlow {
  const idle = state.busy === null;
  switch (event.type) {
    case 'CONNECT':
      return idle && !state.confirm && connectable(event.face)
        ? { ...state, busy: 'connect', problem: null, focusConnect: false }
        : state;
    case 'CONNECT_FAILED':
      return state.busy === 'connect' ? { ...state, busy: null, problem: 'connectFailed' } : state;
    case 'RESTORED':
      return state.busy === 'connect' ? { ...state, busy: null } : state;
    case 'ASK_DISCONNECT':
      return idle && !state.confirm && disconnectable(event.face)
        ? { ...state, confirm: true, problem: null, focusConnect: false }
        : state;
    case 'CANCEL':
      return idle ? { ...state, confirm: false } : state;
    case 'CONFIRM':
      return idle && state.confirm ? { ...state, busy: 'disconnect' } : state;
    case 'DISCONNECTED':
      if (state.busy !== 'disconnect') return state;
      return event.connected
        ? { ...state, busy: null, confirm: false, problem: 'disconnectFailed' }
        : { ...state, busy: null, confirm: false, problem: null, focusConnect: true };
    case 'DISCONNECT_FAILED':
      return state.busy === 'disconnect'
        ? { ...state, busy: null, confirm: false, problem: 'disconnectFailed' }
        : state;
    case 'FACE':
      return idle && state.confirm && !disconnectable(event.face)
        ? { ...state, confirm: false }
        : state;
    case 'DEACTIVATED':
      // An act in flight keeps its slot so its answer lands; everything on screen is dropped.
      return { ...GOOGLE_CALENDAR_IDLE, busy: state.busy };
    case 'ACTIVATED':
    case 'FOCUS_TAKEN':
      return state.focusConnect ? { ...state, focusConnect: false } : state;
  }
}
